import { MOBILE_SECTION_TABS_THEME_VARS } from "@circle-react-shared/uikit/Header/headerThemeVars";
import { useNavigationLinks } from "@circle-react/components/shared/uikit/HeaderV3/NavigationBar/hooks/useNavigationLinks";
import { isPathActive } from "@circle-react/components/shared/uikit/HeaderV3/NavigationBar/utils/urlHelpers";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { useIsCustomColorTheme } from "@circle-react/hooks/useIsCustomColorTheme";
import { MobileShell } from "@circleco/compass/components/MobileShell";
import { Skeleton } from "@circleco/compass/components/Skeleton";
import { Tabs } from "@circleco/compass/components/Tabs";
import type { Tab } from "@circleco/compass/components/Tabs";
import { useLocation } from "@tanstack/react-router";
import { useMemo } from "react";

import { useSpacesTabLabel } from "@/react/components/Mobile/hooks/useSpacesTabLabel";
import { useSitePageMetadata } from "@/react/routes/Main/useSitePageMetadata";

import { RouterLink } from "../components/RouterLink";
import { useMobileChromeVisibility } from "../hooks/chrome/useMobileChromeVisibility";

const SECTION_TABS_SKELETON_COUNT = 4;

/**
 * Default global navigation tabs (Home, Spaces, Courses, …).
 * Extracted to avoid running the `useNavigationLinks` hook when
 * a page override is active.
 *
 * The "Spaces" tab is injected client-side (it doesn't come from the
 * navigation API) since it's a mobile-only route - desktop uses the
 * sidebar for space navigation. Its label defaults to a translation but
 * respects a per-community override (`home_tabs_custom_names.spaces`) set
 * via the Branded Apps Builder's Home Tabs section, e.g. renaming it "Menu".
 *
 * "Home" stays even when Feed is disabled (`home_page_enabled: false`).
 * Desktop still shows it in the header; it points at the community root
 * (`/` or the default space) rather than `/feed`. A full page load is
 * required for `/` so the Rails layout refreshes (see NavigationItem).
 */
export const DefaultSectionTabs = () => {
  const { links, isLoadingLinks } = useNavigationLinks();
  const pathname = useLocation({ select: location => location.pathname });
  const { currentCommunityMember, currentCommunity } = usePunditUserContext();
  const { isSitePageRoute } = useSitePageMetadata();
  const { isTopBarUnbranded } = useMobileChromeVisibility();
  // Default theme opts out of community branding entirely,
  // falling back to Compass's own default tokens
  const isCustomColorTheme = useIsCustomColorTheme();

  // Non-members on private communities render all links as plain <a> tags,
  // matching the desktop NavigationBar behavior (renderAsAnchor).
  const shouldRenderAsAnchor = Boolean(
    !currentCommunityMember && currentCommunity?.is_private,
  );
  const spacesTabLabel = useSpacesTabLabel();

  const tabs: Tab[] = useMemo(() => {
    const navTabs: Tab[] = links
      .filter(link => link.isPresent && !link.isDisabled)
      .map(link => {
        // Custom links need unique values since multiple can exist and
        // can share the same URL; use the link's id as a stable identifier.
        const value = link.kind === "custom" ? `custom-${link.id}` : link.kind;

        // Custom links always render as <a> - website builder pages
        // don't work with React Router (shows a blank page). Home pointing
        // at `/` does too: the Rails layout won't refresh on a client-side
        // navigation. This matches desktop NavigationItem / NavigationLink.
        const shouldUseRouterLink =
          link.kind !== "custom" &&
          !(link.kind === "home" && link.linkTo === "/") &&
          !shouldRenderAsAnchor;

        return {
          value,
          label: link.label,
          renderLink: shouldUseRouterLink ? (
            <RouterLink
              href={link.linkTo}
              target={link.openInNewWindow ? "_blank" : undefined}
              rel={link.openInNewWindow ? "noopener noreferrer" : undefined}
            />
          ) : (
            <a
              aria-label={link.label}
              href={link.linkTo}
              target={link.openInNewWindow ? "_blank" : undefined}
              rel={link.openInNewWindow ? "noopener noreferrer" : undefined}
            />
          ),
        };
      });

    // Inject "Spaces" tab after "Home" (first tab). The spaces route
    // is mobile-only so the backend navigation API doesn't include it.
    // Skipped on Site Page routes - the page author is in control of the
    // nav there and didn't ask for a Spaces tab.
    if (!isSitePageRoute) {
      const spacesTab: Tab = {
        value: "spaces",
        label: spacesTabLabel,
        renderLink: shouldRenderAsAnchor ? (
          <a aria-label={spacesTabLabel} href="/spaces" />
        ) : (
          <RouterLink href="/spaces" />
        ),
      };

      const homeIndex = navTabs.findIndex(tab => tab.value === "home");
      const insertIndex = homeIndex >= 0 ? homeIndex + 1 : 0;
      navTabs.splice(insertIndex, 0, spacesTab);
    }

    return navTabs;
  }, [links, shouldRenderAsAnchor, isSitePageRoute, spacesTabLabel]);

  const activeTab = useMemo(() => {
    // Check for the /spaces route first (segment-aware, not a bare prefix
    // check - a Site Page slug like `/spaces-landing` or `/spaceship` would
    // otherwise false-positive here even though there's no "spaces" tab in
    // `tabs` for that route).
    if (pathname === "/spaces" || pathname.startsWith("/spaces/")) {
      return "spaces";
    }

    // Find the link whose route matches the current path.
    // Home link is a broad match (feed, space detail, space groups,
    // getting started), so check non-home links first for specificity.
    //
    // Built-in links (events, courses, members, etc.) are matched against
    // the pathname directly, boundary-safe via `isPathActive` (a bare
    // `startsWith` would false-positive on unrelated routes sharing a
    // prefix, e.g. a "/courses" tab matching a "/courses-landing" Site
    // Page). These are checked *before* custom links: a custom nav link
    // whose configured URL happens to resolve to the same path as a
    // built-in route (a stale/duplicate link, for example one that still
    // points at the community's own Feed URL) must never outrank the real
    // route and steal its highlighted tab.
    const isNonHomeCandidate = (link: (typeof links)[number]) =>
      link.kind !== "home" && link.isPresent && !link.isDisabled;

    const nonHomeMatch =
      links.find(
        link =>
          isNonHomeCandidate(link) &&
          link.kind !== "custom" &&
          isPathActive(pathname, link.linkTo),
      ) ??
      links.find(
        link =>
          isNonHomeCandidate(link) && link.kind === "custom" && link.isActive,
      );
    if (nonHomeMatch) {
      return nonHomeMatch.kind === "custom"
        ? `custom-${nonHomeMatch.id}`
        : nonHomeMatch.kind;
    }

    // No specific link matched the pathname - check whether we're on one
    // of Home's broad routes (feed, space detail, space groups, getting
    // started; see `isHomeLinkActive` in useNavigationLinks, which already
    // computes this correctly on the "home" link's `isActive`).
    const homeLink = links.find(link => link.kind === "home");
    if (homeLink?.isActive && tabs.some(tab => tab.value === "home")) {
      return "home";
    }

    return "";
  }, [links, pathname, tabs]);

  if (isLoadingLinks) {
    return (
      <MobileShell.HeaderBar
        border="on-scroll"
        themeVars={
          isTopBarUnbranded || !isCustomColorTheme
            ? undefined
            : MOBILE_SECTION_TABS_THEME_VARS
        }
      >
        <div className="flex items-center gap-2">
          {Array.from({ length: SECTION_TABS_SKELETON_COUNT }, (_, index) => (
            <Skeleton
              key={`section-tabs-skeleton-${index}`}
              shape="square"
              className="h-8 w-16 shrink-0"
            />
          ))}
        </div>
      </MobileShell.HeaderBar>
    );
  }

  if (tabs.length === 0) {
    return null;
  }

  return (
    <MobileShell.HeaderBar
      border="on-scroll"
      themeVars={
        isTopBarUnbranded || !isCustomColorTheme
          ? undefined
          : MOBILE_SECTION_TABS_THEME_VARS
      }
    >
      <Tabs.Root tabs={tabs} selectedValue={activeTab} size="md" />
    </MobileShell.HeaderBar>
  );
};
